import { RemoteSignButton } from '../components/RemoteSignButton';
import { AgencySignButton } from '../components/AgencySignButton';
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { propertyInspectionsAPI, propertiesAPI, clientsAPI, tenantAccountsAPI, signaturesAPI } from '../lib/api';
import { 
  ClipboardCheck, Plus, Eye, Trash2, FileText, Search, Filter,
  Home, User, Calendar, CheckCircle2, AlertCircle, XCircle,
  ChevronDown, ChevronUp, Download, PenLine
} from 'lucide-react';
import { SignaturePad } from '../components/SignaturePad';

const PropertyInspections = () => {
  const [inspections, setInspections] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantAccounts, setTenantAccounts] = useState([]);
  const [standardItems, setStandardItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showSignModal, setShowSignModal] = useState(false);
  const [signInspection, setSignInspection] = useState(null);
  const [signatures, setSignatures] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    property_id: '',
    tenant_id: '',
    inspection_type: 'entree',
    inspection_date: new Date().toISOString().slice(0, 16),
    inspector_name: '',
    items: [],
    general_observations: '',
    meter_readings: { electricity: '', water: '', gas: '' }
  });

  // Condition options
  const conditionOptions = [
    { value: 'neuf', label: 'Neuf', color: 'bg-green-500' },
    { value: 'bon', label: 'Bon état', color: 'bg-blue-500' },
    { value: 'usure_normale', label: 'Usure normale', color: 'bg-yellow-500' },
    { value: 'degrade', label: 'Dégradé', color: 'bg-orange-500' },
    { value: 'tres_degrade', label: 'Très dégradé', color: 'bg-red-500' }
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [inspectionsRes, propertiesRes, tenantsRes, itemsRes, accountsRes] = await Promise.all([
        propertyInspectionsAPI.getAll(),
        propertiesAPI.getAll(),
        clientsAPI.getAll({ client_type: 'locataire' }),
        propertyInspectionsAPI.getStandardItems(),
        tenantAccountsAPI.getAll()
      ]);
      setInspections(inspectionsRes.data);
      setProperties(propertiesRes.data);
      setTenants(tenantsRes.data);
      setStandardItems(itemsRes.data);
      setTenantAccounts(accountsRes.data);
      
      // Fetch signatures for all inspections
      const sigMap = {};
      for (const insp of inspectionsRes.data) {
        try {
          const sigRes = await signaturesAPI.get('property_inspection', insp.id);
          if (sigRes.data?.length > 0) sigMap[insp.id] = sigRes.data;
        } catch {}
      }
      setSignatures(sigMap);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Initialize inspection items from standard items
  const initializeItems = useCallback(() => {
    const items = [];
    standardItems.forEach(category => {
      category.items.forEach(item => {
        items.push({
          name: item,
          room: category.category,
          condition: 'bon',
          observations: ''
        });
      });
    });
    return items;
  }, [standardItems]);

  // Filter tenants by selected property using tenant accounts
  const filteredTenants = formData.property_id
    ? tenants.filter(t => {
        // Find tenant accounts linked to the selected property
        const linkedAccount = tenantAccounts.find(
          acc => acc.property_id === formData.property_id && acc.client_id === t.id
        );
        return !!linkedAccount;
      })
    : tenants;

  const handleOpenCreate = () => {
    setFormData({
      property_id: '',
      tenant_id: '',
      inspection_type: 'entree',
      inspection_date: new Date().toISOString().slice(0, 16),
      inspector_name: '',
      items: initializeItems(),
      general_observations: '',
      meter_readings: { electricity: '', water: '', gas: '' }
    });
    setExpandedCategories({});
    setIsCreateDialogOpen(true);
  };

  const handlePropertyChange = (propertyId) => {
    setFormData(prev => ({
      ...prev,
      property_id: propertyId,
      tenant_id: '' // Reset tenant when property changes
    }));
  };

  const handleItemConditionChange = (room, itemName, condition) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.room === room && item.name === itemName
          ? { ...item, condition }
          : item
      )
    }));
  };

  const handleItemObservationChange = (room, itemName, observations) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.room === room && item.name === itemName
          ? { ...item, observations }
          : item
      )
    }));
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.property_id || !formData.tenant_id || !formData.inspector_name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await propertyInspectionsAPI.create(formData);
      toast.success('État des lieux créé avec succès');
      setIsCreateDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error creating inspection:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleView = (inspection) => {
    setSelectedInspection(inspection);
    setIsViewDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet état des lieux ?')) return;

    try {
      await propertyInspectionsAPI.delete(id);
      toast.success('État des lieux supprimé');
      fetchData();
    } catch (error) {
      console.error('Error deleting inspection:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDownloadPdf = async (id) => {
    try {
      const response = await propertyInspectionsAPI.downloadPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `etat_des_lieux_${id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  const handleSignInspection = async (signatureData) => {
    if (!signInspection) return;
    try {
      await signaturesAPI.save({
        document_type: 'property_inspection',
        document_id: signInspection.id,
        signature_data: signatureData
      });
      toast.success('État des lieux signé avec succès');
      setShowSignModal(false);
      setSignInspection(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    }
  };

  const getSignatureStatus = (inspId) => {
    const sigs = signatures[inspId] || [];
    const hasTenant = sigs.some(s => s.signer_type === 'tenant');
    const hasAdmin = sigs.some(s => s.signer_type === 'admin' || s.signer_type === 'agent');
    return { hasTenant, hasAdmin, count: sigs.length };
  };

  const getConditionBadge = (condition) => {
    const opt = conditionOptions.find(o => o.value === condition);
    if (!opt) return <Badge variant="outline">{condition}</Badge>;
    return (
      <Badge className={`${opt.color} text-white`}>
        {opt.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Filter inspections
  const filteredInspections = inspections.filter(insp => {
    const matchesSearch = 
      insp.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insp.property_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insp.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || insp.inspection_type === filterType;
    return matchesSearch && matchesType;
  });

  // Group items by room for display
  const groupItemsByRoom = (items) => {
    const grouped = {};
    items?.forEach(item => {
      if (!grouped[item.room]) {
        grouped[item.room] = [];
      }
      grouped[item.room].push(item);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="property-inspections-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardCheck className="h-7 w-7 text-blue-600" />
            États des Lieux
          </h1>
          <p className="text-gray-500 mt-1">Gérez les états des lieux d'entrée et de sortie</p>
        </div>
        <Button onClick={handleOpenCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="create-inspection-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nouvel État des Lieux
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Rechercher par référence, bien ou locataire..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-type">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="entree">Entrée</SelectItem>
                <SelectItem value="sortie">Sortie</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-2xl font-bold">{inspections.length}</p>
              </div>
              <ClipboardCheck className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Entrées</p>
                <p className="text-2xl font-bold text-green-600">
                  {inspections.filter(i => i.inspection_type === 'entree').length}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Sorties</p>
                <p className="text-2xl font-bold text-orange-600">
                  {inspections.filter(i => i.inspection_type === 'sortie').length}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inspections List */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des États des Lieux ({filteredInspections.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredInspections.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ClipboardCheck className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Aucun état des lieux trouvé</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredInspections.map(inspection => (
                <div
                  key={inspection.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  data-testid={`inspection-item-${inspection.id}`}
                >
                  <div className="flex flex-col sm:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-mono text-sm text-gray-500">{inspection.reference}</span>
                        <Badge variant={inspection.inspection_type === 'entree' ? 'default' : 'secondary'}>
                          {inspection.inspection_type === 'entree' ? 'Entrée' : 'Sortie'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1">
                          <Home className="h-4 w-4 text-gray-400" />
                          {inspection.property_title || 'N/A'}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4 text-gray-400" />
                          {inspection.tenant_name || 'N/A'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          {formatDate(inspection.inspection_date)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(() => {
                        const sigStatus = getSignatureStatus(inspection.id);
                        if (sigStatus.count > 0) {
                          return (
                            <Badge variant="outline" className="text-green-600 border-green-300 text-xs gap-1">
                              <PenLine className="w-3 h-3" /> Signé
                            </Badge>
                          );
                        }
                        return null;
                      })()}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setSignInspection(inspection); setShowSignModal(true); }}
                        className="text-[#1B3A4B] border-[#1B3A4B]/30"
                        data-testid={`sign-btn-${inspection.id}`}
                        title="Signer"
                      >
                        <PenLine className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleView(inspection)}
                        data-testid={`view-btn-${inspection.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadPdf(inspection.id)}
                        data-testid={`pdf-btn-${inspection.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <RemoteSignButton compact docType="inspection" docId={inspection.id} />
                      <AgencySignButton docType="inspection" docId={inspection.id} />
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(inspection.id)}
                        data-testid={`delete-btn-${inspection.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5" />
              Nouvel État des Lieux
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Bien immobilier *</Label>
                <Select value={formData.property_id} onValueChange={handlePropertyChange}>
                  <SelectTrigger data-testid="select-property">
                    <SelectValue placeholder="Sélectionner un bien" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map(prop => (
                      <SelectItem key={prop.id} value={prop.id}>
                        {prop.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Locataire *</Label>
                <Select 
                  value={formData.tenant_id} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, tenant_id: v }))}
                  disabled={!formData.property_id}
                >
                  <SelectTrigger data-testid="select-tenant">
                    <SelectValue placeholder={formData.property_id ? "Sélectionner un locataire" : "Sélectionnez d'abord un bien"} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredTenants.map(tenant => (
                      <SelectItem key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Type d'état des lieux *</Label>
                <Select 
                  value={formData.inspection_type} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, inspection_type: v }))}
                >
                  <SelectTrigger data-testid="select-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="entree">Entrée (Emménagement)</SelectItem>
                    <SelectItem value="sortie">Sortie (Déménagement)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Date et heure *</Label>
                <Input
                  type="datetime-local"
                  value={formData.inspection_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, inspection_date: e.target.value }))}
                  data-testid="input-date"
                />
              </div>

              <div className="md:col-span-2">
                <Label>Représentant de l'agence *</Label>
                <Input
                  value={formData.inspector_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, inspector_name: e.target.value }))}
                  placeholder="Nom du représentant effectuant l'état des lieux"
                  data-testid="input-inspector"
                />
              </div>
            </div>

            {/* Meter Readings */}
            <div>
              <Label className="text-base font-semibold">Relevés de compteurs</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                <div>
                  <Label className="text-sm text-gray-500">Électricité</Label>
                  <Input
                    value={formData.meter_readings.electricity}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      meter_readings: { ...prev.meter_readings, electricity: e.target.value }
                    }))}
                    placeholder="Relevé électricité"
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Eau</Label>
                  <Input
                    value={formData.meter_readings.water}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      meter_readings: { ...prev.meter_readings, water: e.target.value }
                    }))}
                    placeholder="Relevé eau"
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Gaz</Label>
                  <Input
                    value={formData.meter_readings.gas}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      meter_readings: { ...prev.meter_readings, gas: e.target.value }
                    }))}
                    placeholder="Relevé gaz"
                  />
                </div>
              </div>
            </div>

            {/* Inspection Items */}
            <div>
              <Label className="text-base font-semibold">Éléments à inspecter</Label>
              <div className="mt-2 space-y-2">
                {standardItems.map(category => (
                  <div key={category.category} className="border rounded-lg overflow-hidden">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                      onClick={() => toggleCategory(category.category)}
                    >
                      <span className="font-medium">{category.category}</span>
                      {expandedCategories[category.category] ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                    {expandedCategories[category.category] && (
                      <div className="p-3 space-y-3">
                        {category.items.map(itemName => {
                          const itemData = formData.items.find(
                            i => i.room === category.category && i.name === itemName
                          );
                          return (
                            <div key={itemName} className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                              <span className="w-40 text-sm font-medium">{itemName}</span>
                              <Select
                                value={itemData?.condition || 'bon'}
                                onValueChange={(v) => handleItemConditionChange(category.category, itemName, v)}
                              >
                                <SelectTrigger className="w-40">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {conditionOptions.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Input
                                className="flex-1"
                                placeholder="Observations..."
                                value={itemData?.observations || ''}
                                onChange={(e) => handleItemObservationChange(category.category, itemName, e.target.value)}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* General Observations */}
            <div>
              <Label>Observations générales</Label>
              <Textarea
                value={formData.general_observations}
                onChange={(e) => setFormData(prev => ({ ...prev, general_observations: e.target.value }))}
                placeholder="Remarques générales sur l'état du bien..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-btn">
              Créer l'état des lieux
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5" />
              État des Lieux - {selectedInspection?.reference}
            </DialogTitle>
          </DialogHeader>

          {selectedInspection && (
            <div className="space-y-6">
              {/* Info Summary */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Bien</p>
                  <p className="font-medium">{selectedInspection.property_title}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Locataire</p>
                  <p className="font-medium">{selectedInspection.tenant_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  <Badge variant={selectedInspection.inspection_type === 'entree' ? 'default' : 'secondary'}>
                    {selectedInspection.inspection_type === 'entree' ? 'Entrée' : 'Sortie'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium">{formatDate(selectedInspection.inspection_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Représentant</p>
                  <p className="font-medium">{selectedInspection.inspector_name}</p>
                </div>
              </div>

              {/* Meter Readings */}
              {selectedInspection.meter_readings && (
                <div>
                  <h3 className="font-semibold mb-2">Relevés de compteurs</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {selectedInspection.meter_readings.electricity && (
                      <div className="p-3 bg-gray-50 rounded">
                        <p className="text-sm text-gray-500">Électricité</p>
                        <p className="font-medium">{selectedInspection.meter_readings.electricity}</p>
                      </div>
                    )}
                    {selectedInspection.meter_readings.water && (
                      <div className="p-3 bg-gray-50 rounded">
                        <p className="text-sm text-gray-500">Eau</p>
                        <p className="font-medium">{selectedInspection.meter_readings.water}</p>
                      </div>
                    )}
                    {selectedInspection.meter_readings.gas && (
                      <div className="p-3 bg-gray-50 rounded">
                        <p className="text-sm text-gray-500">Gaz</p>
                        <p className="font-medium">{selectedInspection.meter_readings.gas}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Items by Room */}
              <div>
                <h3 className="font-semibold mb-2">Détail de l'inspection</h3>
                {Object.entries(groupItemsByRoom(selectedInspection.items)).map(([room, items]) => (
                  <div key={room} className="mb-4">
                    <h4 className="font-medium text-gray-700 mb-2">{room}</h4>
                    <div className="space-y-2">
                      {items.map((item, idx) => (
                        <div key={`${room}-${item.name || idx}`} className="flex items-center gap-4 p-2 bg-gray-50 rounded">
                          <span className="w-40">{item.name}</span>
                          {getConditionBadge(item.condition)}
                          {item.observations && (
                            <span className="text-sm text-gray-500 italic">{item.observations}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* General Observations */}
              {selectedInspection.general_observations && (
                <div>
                  <h3 className="font-semibold mb-2">Observations générales</h3>
                  <p className="p-3 bg-gray-50 rounded">{selectedInspection.general_observations}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleDownloadPdf(selectedInspection?.id)}
            >
              <FileText className="h-4 w-4 mr-2" />
              Télécharger PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => { setSignInspection(selectedInspection); setShowSignModal(true); setIsViewDialogOpen(false); }}
              className="text-[#1B3A4B] border-[#1B3A4B]/30"
            >
              <PenLine className="h-4 w-4 mr-2" />
              Signer
            </Button>
            <Button onClick={() => setIsViewDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature Modal */}
      <Dialog open={showSignModal} onOpenChange={(open) => { setShowSignModal(open); if (!open) setSignInspection(null); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5 text-[#1B3A4B]" />
              Signer l'État des Lieux
            </DialogTitle>
            <DialogDescription>
              {signInspection && `${signInspection.reference} - ${signInspection.property_title}`}
            </DialogDescription>
          </DialogHeader>
          <SignaturePad
            onSave={handleSignInspection}
            onCancel={() => { setShowSignModal(false); setSignInspection(null); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyInspections;
